import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Search, ShieldCheck, Save, TrendingUp, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIER_C = {
  unverified: 'bg-gray-100 text-gray-600', part_verified: 'bg-amber-100 text-amber-700',
  active_verified: 'bg-[#00A79D]/10 text-[#00857D]', tested_proven: 'bg-[#00A79D] text-white',
};

const Check = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid={testid}>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#00A79D]" />
    {label}
  </label>
);

const SupplierTrust = () => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [sel, setSel] = useState(null);
  const [marks, setMarks] = useState(null);
  const [tier, setTier] = useState(null);
  const [gap, setGap] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { axios.get(`${API}/trust/meta`).then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const doSearch = useCallback(async () => {
    const { data } = await axios.get(`${API}/trust/suppliers`, { params: { search, limit: 25 } });
    setResults(data.items || []);
  }, [search]);
  useEffect(() => { doSearch(); }, [doSearch]);

  const openSupplier = async (id) => {
    const { data } = await axios.get(`${API}/trust/suppliers/${id}`);
    setSel(data); setMarks(data.marks); setTier(data.tier); setGap(data.gap);
  };

  const setComp = (k, field, v) => setMarks((p) => ({ ...p, compliance: { ...p.compliance, [k]: { ...p.compliance[k], [field]: v } } }));
  const setM = (k, v) => setMarks((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/trust/suppliers/${sel.id}`, { marks });
      setTier(data.tier); setGap(data.gap);
      toast.success(`Saved - tier: ${data.label}`);
      doSearch();
    } catch { toast.error('Could not save'); } finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="supplier-trust-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Supplier Trust</h1>
        <p className="text-gray-500 text-sm mt-1">Record evidenced marks per supplier. The trust tier is derived automatically - commercial state never affects it.</p>
      </div>

      {meta && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="trust-tier-counts">
          {['unverified', 'part_verified', 'active_verified', 'tested_proven'].map((t) => (
            <Card key={t}><CardContent className="p-4"><p className="text-2xl font-bold text-gray-900">{meta.counts?.[t] || 0}</p><p className="text-sm text-gray-500">{meta.tier_labels[t]}</p></CardContent></Card>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-[340px_1fr] gap-6">
        {/* Search list */}
        <div>
          <div className="flex gap-2 mb-3">
            <Input placeholder="Search suppliers" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="trust-search" />
            <Button variant="outline" onClick={doSearch} className="shrink-0"><Search className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {results.map((r) => (
              <button key={r.id} onClick={() => openSupplier(r.id)}
                className={`w-full text-left flex items-center justify-between gap-2 border rounded-lg px-3 py-2.5 transition-colors ${sel?.id === r.id ? 'border-[#00A79D] bg-[#00A79D]/5' : 'border-[#E5E7EB] hover:border-[#00A79D]/40'}`}
                data-testid={`trust-supplier-${r.id}`}>
                <span className="text-sm font-medium text-gray-800 truncate">{r.name}</span>
                <Badge className={`${TIER_C[r.tier]} text-[10px] shrink-0`}>{r.label}</Badge>
              </button>
            ))}
            {results.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">No suppliers found.</p>}
          </div>
        </div>

        {/* Editor */}
        <div>
          {!sel || !marks ? (
            <div className="border border-dashed border-gray-200 rounded-lg py-20 text-center text-gray-400" data-testid="trust-empty">Select a supplier to record trust marks.</div>
          ) : (
            <Card data-testid="trust-editor"><CardContent className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{sel.name}</h2>
                  <div className="mt-1"><Badge className={`${TIER_C[tier]} inline-flex items-center gap-1`}><ShieldCheck className="w-3.5 h-3.5" /> {meta?.tier_labels[tier]}</Badge></div>
                </div>
                <Button onClick={save} disabled={saving} className="bg-[#00A79D] hover:bg-[#00857D]" data-testid="trust-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
                </Button>
              </div>

              {gap?.next && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="trust-gap">
                  <p className="text-sm font-semibold text-amber-800 inline-flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> To reach {meta?.tier_labels[gap.next]}:</p>
                  <ul className="mt-2 space-y-1 text-sm text-amber-800">{gap.requirements.map((r, i) => <li key={i}>• {r}</li>)}</ul>
                </div>
              )}

              <div>
                <p className="text-sm font-semibold text-gray-900 mb-2">Compliance marks</p>
                <p className="text-xs text-gray-500 mb-3 inline-flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Verify against the issuing register, not the supplier's website.</p>
                <div className="space-y-3">
                  {(sel.compliance_marks || []).map((m) => (
                    <div key={m.key} className="border border-[#E5E7EB] rounded-lg p-3" data-testid={`trust-comp-${m.key}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium text-gray-800">{m.label}</span>
                        <div className="flex items-center gap-4">
                          <Check label="Claimed" checked={marks.compliance[m.key]?.claimed} onChange={(v) => setComp(m.key, 'claimed', v)} testid={`trust-${m.key}-claimed`} />
                          <Check label="Verified" checked={marks.compliance[m.key]?.verified} onChange={(v) => setComp(m.key, 'verified', v)} testid={`trust-${m.key}-verified`} />
                        </div>
                      </div>
                      <Input placeholder="Evidence note" value={marks.compliance[m.key]?.note || ''} onChange={(e) => setComp(m.key, 'note', e.target.value)} className="mt-2 h-8 text-sm" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 border-t border-gray-100 pt-4">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-900">Verification meeting</p>
                  <Check label="Meeting held" checked={marks.verification_meeting?.held} onChange={(v) => setM('verification_meeting', { ...marks.verification_meeting, held: v })} testid="trust-meeting-held" />
                  <Check label="Written up (register)" checked={marks.verification_meeting?.writeup} onChange={(v) => setM('verification_meeting', { ...marks.verification_meeting, writeup: v })} testid="trust-meeting-writeup" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-900">Assurance report</p>
                  <Check label="Published" checked={marks.assurance_report?.published} onChange={(v) => setM('assurance_report', { ...marks.assurance_report, published: v })} testid="trust-report-published" />
                  <Input placeholder="Report URL" value={marks.assurance_report?.url || ''} onChange={(e) => setM('assurance_report', { ...marks.assurance_report, url: e.target.value })} className="h-8 text-sm" />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4 border-t border-gray-100 pt-4">
                <Check label="References validated" checked={marks.references_validated} onChange={(v) => setM('references_validated', v)} testid="trust-references" />
                <Check label="Listing fresh / up to date" checked={marks.freshness_ok} onChange={(v) => setM('freshness_ok', v)} testid="trust-freshness" />
                <div>
                  <label className="text-xs font-semibold text-gray-500">Claims ratio (0-1)</label>
                  <Input type="number" min="0" max="1" step="0.05" value={marks.claims_ratio} onChange={(e) => setM('claims_ratio', e.target.value)} className="h-8 text-sm mt-1" data-testid="trust-claims-ratio" />
                </div>
              </div>
            </CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierTrust;
